/**
 * GUMSHOE — the case file: "The Last Set at the Blue Dahlia".
 *
 * Pure data, no logic. Vivian Lake, torch singer at the Blue Dahlia, found
 * dead in the alley behind the club. Three suspects, six real clues, three
 * red herrings, and a dependency graph that makes the detective earn it.
 *
 * The state machine lives in index.ts / state.ts. Owned by GAME.
 */
import type { ClueDef, MonologueBeat, SuspectDef } from '../core/types'

// ---------------------------------------------------------------------------
// Monologue pacing
// ---------------------------------------------------------------------------

/** Reading pace for caption + radio-voice murmur. */
const MONOLOGUE_MS_PER_CHAR = 55
const MONOLOGUE_MIN_MS = 2600
const MONOLOGUE_MAX_MS = 12000

const beat = (id: string, text: string): MonologueBeat => ({
  id,
  text,
  durationMs: Math.min(
    MONOLOGUE_MAX_MS,
    Math.max(MONOLOGUE_MIN_MS, Math.round(text.length * MONOLOGUE_MS_PER_CHAR))
  )
})

// ---------------------------------------------------------------------------
// Suspects
// ---------------------------------------------------------------------------

export const SUSPECT_MAXIE = 'maxie-stern'
export const SUSPECT_JOHNNY = 'johnny-favors'
export const SUSPECT_DOLORES = 'dolores-vane'

/** The six real clues. Order here is the canonical journal order. */
export const REAL_CLUE_IDS: readonly string[] = [
  'matchbook',
  'torn-photograph',
  'monogrammed-cufflink',
  'ledger-page',
  'threatening-note',
  'pawn-ticket'
]

export const SUSPECTS: readonly SuspectDef[] = [
  {
    id: SUSPECT_MAXIE,
    name: 'Maxie Stern',
    description:
      'Owner of the Blue Dahlia. Wears his smile like a rented tux and keeps ' +
      'two sets of books the way other men keep two girlfriends.',
    isGuilty: true,
    requiredClueIds: REAL_CLUE_IDS
  },
  {
    id: SUSPECT_JOHNNY,
    name: 'Johnny Favors',
    description:
      'The crooner. A voice like warm brandy and a spine like wet cardboard. ' +
      'Loved Vivian from the second microphone away.',
    isGuilty: false,
    requiredClueIds: REAL_CLUE_IDS
  },
  {
    id: SUSPECT_DOLORES,
    name: 'Dolores Vane',
    description:
      'The headliner Vivian replaced. Still owns the alley behind the club, ' +
      'one cigarette at a time. Her hate is the honest kind — loud, and useless.',
    isGuilty: false,
    requiredClueIds: REAL_CLUE_IDS
  }
]

// ---------------------------------------------------------------------------
// Clues — six real, three red herrings
// ---------------------------------------------------------------------------

export const CLUES: readonly ClueDef[] = [
  // --- Real ---
  {
    id: 'matchbook',
    name: 'Blue Dahlia Matchbook',
    description:
      'Half the matches torn out. Inside the cover, a phone number in pencil, ' +
      'written by a hand heavy enough to leave a groove in the cardboard.',
    position: { x: 7.4, y: 0.17, z: -10.6 },
    zone: 'outdoor',
    monologueId: 'beat-matchbook',
    suspectId: SUSPECT_MAXIE
  },
  {
    id: 'torn-photograph',
    name: 'Torn Photograph',
    description:
      'Vivian, laughing, somebody’s arm around her shoulders. The face is torn ' +
      'away, but the wrist wears a cufflink that caught the flashbulb.',
    position: { x: -7.6, y: 0.16, z: 14 },
    zone: 'outdoor',
    monologueId: 'beat-torn-photograph'
  },
  {
    id: 'monogrammed-cufflink',
    name: 'Monogrammed Cufflink',
    description:
      'Gold, heavy, initials M.S. The match to the one in the torn photograph. ' +
      'Found in the gutter by the stage door, where bedrooms aren’t.',
    position: { x: 0.9, y: 0.02, z: -33.6 },
    zone: 'outdoor',
    monologueId: 'beat-monogrammed-cufflink',
    suspectId: SUSPECT_MAXIE
  },
  {
    id: 'ledger-page',
    name: 'Ledger Page',
    description:
      'A page from the Dahlia’s books, mailed to the office in Vivian’s hand. ' +
      'Two sets of numbers for the same week. The club was bleeding into somebody’s pocket.',
    position: { x: 37.5, y: 0.85, z: -0.95 },
    zone: 'indoor',
    monologueId: 'beat-ledger-page',
    suspectId: SUSPECT_MAXIE
  },
  {
    id: 'threatening-note',
    name: 'Threatening Note',
    description:
      '“Sing about the books again and you’ll sing nowhere.” Unsigned. The pencil ' +
      'grooved the page — the same heavy hand that wrote the number in the matchbook.',
    position: { x: 39.5, y: 1.33, z: 4.4 },
    zone: 'indoor',
    monologueId: 'beat-threatening-note',
    suspectId: SUSPECT_MAXIE
  },
  {
    id: 'pawn-ticket',
    name: 'Pawn Ticket',
    description:
      'A ticket for a .38, redeemed the day Vivian died. The counter signature is ' +
      'rushed but readable: M. Stern.',
    position: { x: 20, y: 0.03, z: 1.4 },
    zone: 'outdoor',
    monologueId: 'beat-pawn-ticket',
    suspectId: SUSPECT_MAXIE
  },
  // --- Red herrings ---
  {
    id: 'lipstick-cigarette',
    name: 'Lipstick-Stained Cigarette',
    description:
      'Stubbed out in lipstick the color of a fire alarm. Dolores Vane’s shade. ' +
      'She smokes in this alley every night. Hate’s cheap; everybody’s got some.',
    position: { x: 12.5, y: 0.02, z: 1.8 },
    zone: 'outdoor',
    monologueId: 'beat-lipstick-cigarette',
    suspectId: SUSPECT_DOLORES
  },
  {
    id: 'love-letter',
    name: 'Unsent Love Letter',
    description:
      'Johnny Favors’ hand, all aching adjectives and no spine. He loved her the ' +
      'way a moth loves a porch light. Moths don’t kill the light.',
    position: { x: 31, y: 0.03, z: -4.2 },
    zone: 'indoor',
    monologueId: 'beat-love-letter',
    suspectId: SUSPECT_JOHNNY
  },
  {
    id: 'racing-form',
    name: 'Racing Form',
    description:
      'Half the field circled in desperation. Johnny owes money to men who don’t ' +
      'send reminder notices. But broke and guilty are different neighborhoods.',
    position: { x: -8.2, y: 0.16, z: -4 },
    zone: 'outdoor',
    monologueId: 'beat-racing-form',
    suspectId: SUSPECT_JOHNNY
  }
]

// ---------------------------------------------------------------------------
// Clue dependency graph — a clue is collectible only after its prerequisites
// ---------------------------------------------------------------------------

export const CLUE_PREREQS: Readonly<Record<string, readonly string[]>> = {
  /** You only know the cufflink matters after seeing it in the photograph. */
  'monogrammed-cufflink': ['torn-photograph'],
  /** The note reads as a threat once the ledger and matchbook hand match up. */
  'threatening-note': ['ledger-page', 'matchbook'],
  /** The ticket only means murder once the cufflink puts Stern in the alley. */
  'pawn-ticket': ['monogrammed-cufflink']
}

// ---------------------------------------------------------------------------
// Monologue beats
// ---------------------------------------------------------------------------

export const BEAT_INTRO = 'beat-intro'
export const BEAT_OFFICE = 'beat-office'
export const BEAT_BOOTH_READY = 'beat-booth-ready'
export const BEAT_BOOTH_EARLY = 'beat-booth-early'
export const BEAT_CLUE_LOCKED = 'beat-clue-locked'

export const MONOLOGUE_BEATS: readonly MonologueBeat[] = [
  beat(
    BEAT_INTRO,
    'Rain again. It always rains when somebody dies badly. Vivian Lake sang her ' +
      'last set at the Blue Dahlia and finished the night face-down in the alley ' +
      'behind it. The cops called it a robbery. The cops call everything a robbery. ' +
      'Somebody paid me fifty dollars to call it what it was.'
  ),
  beat(
    BEAT_OFFICE,
    'My office. Frosted glass, a bottle in the desk, and a rent bill with opinions. ' +
      'The city looked better through the blinds — sliced thin, like everything ' +
      'else in this town.'
  ),
  beat(
    BEAT_BOOTH_EARLY,
    'The phone booth waited on the corner like a confessional. I had a nickel and ' +
      'half a story. You don’t spend either until the story’s whole.'
  ),
  beat(
    BEAT_BOOTH_READY,
    'Six pieces, one frame, and every edge fit. Time to drop the nickel and say a ' +
      'name. In this town you only get one call like this — better make it the ' +
      'right name.'
  ),
  beat(
    BEAT_CLUE_LOCKED,
    'It caught my eye, but it didn’t talk yet. Evidence is like a witness — lean ' +
      'on it too early and it clams up. I needed something else first.'
  ),
  beat(
    'beat-matchbook',
    'A Blue Dahlia matchbook, half the matches gone. Inside the cover, a phone ' +
      'number in pencil — written by a hand heavy enough to leave a groove. ' +
      'Whoever wrote it wasn’t asking. They were telling.'
  ),
  beat(
    'beat-torn-photograph',
    'A photograph of Vivian, laughing, somebody’s arm around her shoulders. The ' +
      'face was torn away, but the wrist wore a cufflink that caught the flashbulb ' +
      'like a wink. People tear faces out of pictures for one of two reasons. ' +
      'Neither of them is love.'
  ),
  beat(
    'beat-monogrammed-cufflink',
    'There it was in the gutter — the cufflink from the photograph. Gold, ' +
      'monogrammed M.S. Men lose cufflinks in two places: bedrooms and struggles. ' +
      'The alley behind a nightclub isn’t a bedroom.'
  ),
  beat(
    'beat-ledger-page',
    'The morning mail had brought me a page from the Dahlia’s ledger, Vivian’s ' +
      'handwriting on the envelope. Two sets of numbers for the same week. The ' +
      'club was bleeding money into somebody’s pocket, and Vivian had learned ' +
      'to count.'
  ),
  beat(
    'beat-threatening-note',
    'Folded into the case file, a note: sing about the books again and you’ll ' +
      'sing nowhere. No signature. It didn’t need one. The pencil had pressed hard ' +
      'enough to groove the page — the same heavy hand from the matchbook.'
  ),
  beat(
    'beat-pawn-ticket',
    'A pawn ticket for a .38, redeemed the day Vivian died. The counter signature ' +
      'was rushed but readable: M. Stern. Maxie got his gun back the same ' +
      'afternoon his singer learned arithmetic.'
  ),
  beat(
    'beat-lipstick-cigarette',
    'A cigarette stubbed out in lipstick the color of a fire alarm. Dolores ' +
      'Vane’s shade — she smoked in this alley every night, hating Vivian a ' +
      'little more with each drag. Hate’s cheap, though. Everybody’s got some.'
  ),
  beat(
    'beat-love-letter',
    'An unsent letter in Johnny Favors’ hand, all aching adjectives and no ' +
      'spine. He loved her the way a moth loves a porch light. Moths don’t kill ' +
      'the light. They just circle it.'
  ),
  beat(
    'beat-racing-form',
    'A racing form with half the field circled in desperation. Johnny owed money ' +
      'to men who don’t send reminder notices. But broke and guilty are two ' +
      'different neighborhoods.'
  )
]

// ---------------------------------------------------------------------------
// Phone booth interact ids — WORLD tags the booth mesh; we accept any of these
// ---------------------------------------------------------------------------

export const PHONE_BOOTH_INTERACT_IDS: ReadonlySet<string> = new Set([
  'phone-booth',
  'phonebooth',
  'phone_booth',
  'payphone',
  'prop:phone-booth',
  'booth'
])
